import { useState, useEffect } from "react";
import { SubcategoryDetailProps, CardType, FieldAdvisorCardType, PromoteCardType } from "../constants/settingsStructure";
import { ChevronRight } from "lucide-react";
import { cn } from "@/modern/utils";
import AIPromptDetail from "./AIPromptDetail";
import RevertToFactoryButton from "../components/RevertToFactoryButton";
import TestPromptButton from "../components/TestPromptButton";
// @ts-expect-error - userPreferences is dynamically set in bryntumContext
import { userPreferences } from "@/react/context/bryntumContext.jsx";
import { PERSISTENCE_STATE_KEYS } from "@/other/constants/PersistenceStateKeys";

// Define prompt options for inspection types (site_safety, workmanship)
interface InspectionPromptOption {
  id: string;
  cardType: CardType;
  label: string;
  description: string;
  required: boolean;
}

// Define prompt options for advisor types (analyze, execute, review)
interface AdvisorPromptOption {
  id: string;
  fieldAdvisorCardType: FieldAdvisorCardType;
  label: string;
  description: string;
  required: boolean;
}

// Define prompt options for promote type
interface PromotePromptOption {
  id: string;
  promoteCardType: PromoteCardType;
  label: string;
  description: string;
  required: boolean;
}

// Inspection prompt options (for site_safety and workmanship)
export const INSPECTION_PROMPT_OPTIONS: InspectionPromptOption[] = [
  {
    id: "role",
    cardType: "role",
    label: "Role",
    description: "Define the AI's role and responsibilities for this inspection type",
    required: true
  },
  {
    id: "site_context",
    cardType: "site_context",
    label: "Site Context",
    description: "Provide context about the site and project environment",
    required: true
  },
  {
    id: "focus_and_exclusions",
    cardType: "focus_and_exclusions",
    label: "Focus & Exclusions",
    description: "Specify what to focus on and what to exclude",
    required: false
  },
  {
    id: "conditional_instructions",
    cardType: "conditional_instructions",
    label: "Conditional Instructions",
    description: "Set conditional logic for different scenarios",
    required: true
  },
  {
    id: "risk_and_tolerance",
    cardType: "risk_and_tolerance",
    label: "Risk & Tolerance",
    description: "Define risk tolerance levels and thresholds",
    required: false
  },
  {
    id: "insight_intent",
    cardType: "insight_intent",
    label: "Insight Intent",
    description: "Specify the intent and depth of insights to generate",
    required: false
  },
  {
    id: "constraints",
    cardType: "constraints",
    label: "Constraints",
    description: "Define limitations and constraints for the AI",
    required: true
  },
  {
    id: "output_content",
    cardType: "output_content",
    label: "Output Content",
    description: "Configure the format and content of AI outputs",
    required: false
  },
  {
    id: "quality_check",
    cardType: "quality_check",
    label: "Quality Check",
    description: "Set quality assurance criteria and checks",
    required: false
  },
  {
    id: "trade_appendices",
    cardType: "trade_appendices",
    label: "Trade Appendices",
    description: "Add trade-specific appendices and references",
    required: false
  }
];

// Advisor prompt options (for analyze, execute, review)
export const ADVISOR_PROMPT_OPTIONS: AdvisorPromptOption[] = [
  {
    id: "persona",
    fieldAdvisorCardType: "persona",
    label: "Persona",
    description: "Define the AI advisor's personality and expertise",
    required: true
  },
  {
    id: "context",
    fieldAdvisorCardType: "context",
    label: "Context",
    description: "Provide background context for the advisor",
    required: true
  },
  {
    id: "core_goal",
    fieldAdvisorCardType: "core_goal",
    label: "Core Goal",
    description: "Specify the primary goal and objectives",
    required: true
  },
  {
    id: "decision_priorities",
    fieldAdvisorCardType: "decision_priorities",
    label: "Decision Priorities",
    description: "Set priorities for decision-making",
    required: false
  },
  {
    id: "special_skills",
    fieldAdvisorCardType: "special_skills",
    label: "Special Skills",
    description: "Define specialized skills and capabilities",
    required: false
  },
  {
    id: "use_of_documents",
    fieldAdvisorCardType: "use_of_documents",
    label: "Use of Documents",
    description: "Configure how documents should be utilized",
    required: false
  },
  {
    id: "tone_and_teaching",
    fieldAdvisorCardType: "tone_and_teaching",
    label: "Tone & Teaching",
    description: "Set communication tone and teaching approach",
    required: false
  },
  {
    id: "output_content",
    fieldAdvisorCardType: "output_content",
    label: "Output Content",
    description: "Configure the format and content of outputs",
    required: false
  },
  {
    id: "questions_to_answer",
    fieldAdvisorCardType: "questions_to_answer",
    label: "Questions to Answer",
    description: "Specify key questions the advisor should address",
    required: false
  },
  {
    id: "limits",
    fieldAdvisorCardType: "limits",
    label: "Limits",
    description: "Define boundaries and limitations",
    required: false
  }
];

// Promote prompt options
export const PROMOTE_PROMPT_OPTIONS: PromotePromptOption[] = [
  { id: "role", promoteCardType: "role", label: "Role", description: "Define the AI's role for classifying and tagging construction images", required: true },
  { id: "domain_context", promoteCardType: "domain_context", label: "Domain Context", description: "Provide context about the construction domain and environment", required: true },
  { id: "declared_intents", promoteCardType: "declared_intents", label: "Declared Intents", description: "Specify metadata generation goals: caption, alt text, tags", required: true },
  { id: "tagging_philosophy", promoteCardType: "tagging_philosophy", label: "Tagging Philosophy", description: "Define the tag taxonomy and classification conventions", required: false },
  { id: "folder_suggestion_logic", promoteCardType: "folder_suggestion_logic", label: "Folder Suggestion Logic", description: "Configure folder placement based on content tags", required: false },
  { id: "temporal_classification", promoteCardType: "temporal_classification", label: "Temporal Classification", description: "Define rules for before, during, and after classification", required: false },
  { id: "tagging_rules_and_restraint", promoteCardType: "tagging_rules_and_restraint", label: "Tagging Rules and Restraint", description: "Set conservative tagging policies and evidence requirements", required: false },
  { id: "restriction_and_visibility_rules", promoteCardType: "restriction_and_visibility_rules", label: "Restriction and Visibility Rules", description: "Configure sensitivity flagging for content, faces, and safety", required: false },
  { id: "editorial_output_rules", promoteCardType: "editorial_output_rules", label: "Editorial Output Rules", description: "Set tone and style guidelines for captions and alt text", required: false },
];

type AnyPromptOption = InspectionPromptOption | AdvisorPromptOption | PromotePromptOption;

const AIPromptSelector = ({ subcategory }: SubcategoryDetailProps) => {
  const [selectedOption, setSelectedOption] = useState<AnyPromptOption | null>(null);

  // Determine which type of prompt options to show
  const inspectionType = subcategory.inspectionType;
  const advisorType = subcategory.advisorType;
  const promoteType = subcategory.promoteType;
  const isPromoteType = !!promoteType;
  const isInspectionType = !advisorType && !promoteType && !!inspectionType;

  // Reset selectedOption when subcategory changes or selection becomes invalid
  useEffect(() => {
    if (selectedOption) {
      const isValid =
        (isPromoteType && 'promoteCardType' in selectedOption) ||
        (isInspectionType && 'cardType' in selectedOption) ||
        (!isInspectionType && !isPromoteType && 'fieldAdvisorCardType' in selectedOption);
      if (!isValid) {
        setSelectedOption(null);
      }
    }
  }, [subcategory.id, selectedOption, isInspectionType, isPromoteType]);

  // Validate that we have at least one type
  if (!inspectionType && !advisorType && !promoteType) {
    console.error("AIPromptSelector requires inspectionType, advisorType, or promoteType", subcategory);
    return (
      <div className="p-6">
        <p className="text-destructive">Configuration error: Missing prompt type</p>
      </div>
    );
  }

  // Get the correct options list based on type
  const promptOptions: AnyPromptOption[] = isPromoteType
    ? PROMOTE_PROMPT_OPTIONS
    : isInspectionType
      ? INSPECTION_PROMPT_OPTIONS
      : ADVISOR_PROMPT_OPTIONS;

  // Validate that selectedOption is compatible with current prompt type
  const isSelectedOptionValid = selectedOption && (
    (isPromoteType && 'promoteCardType' in selectedOption) ||
    (isInspectionType && 'cardType' in selectedOption) ||
    (!isInspectionType && !isPromoteType && 'fieldAdvisorCardType' in selectedOption)
  );

  if (selectedOption && isSelectedOptionValid) {
    let modifiedSubcategory: typeof subcategory;

    if (isPromoteType) {
      modifiedSubcategory = {
        ...subcategory,
        label: selectedOption.label,
        required: selectedOption.required,
        promoteCardType: (selectedOption as PromotePromptOption).promoteCardType,
        promoteType: subcategory.promoteType,
        inspectionType: undefined,
        cardType: undefined,
        advisorType: undefined,
        fieldAdvisorCardType: undefined
      };
    } else if (isInspectionType) {
      modifiedSubcategory = {
        ...subcategory,
        label: selectedOption.label,
        required: selectedOption.required,
        cardType: (selectedOption as InspectionPromptOption).cardType,
        inspectionType: subcategory.inspectionType,
        advisorType: undefined,
        fieldAdvisorCardType: undefined,
        promoteType: undefined,
        promoteCardType: undefined
      };
    } else {
      modifiedSubcategory = {
        ...subcategory,
        label: selectedOption.label,
        required: selectedOption.required,
        fieldAdvisorCardType: (selectedOption as AdvisorPromptOption).fieldAdvisorCardType,
        advisorType: subcategory.advisorType,
        inspectionType: undefined,
        cardType: undefined,
        promoteType: undefined,
        promoteCardType: undefined
      };
    }

    return (
      <div className="flex flex-col h-full overflow-hidden">
        <div className="px-6 pt-6 pb-4 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSelectedOption(null)}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors px-3 py-2 hover:bg-accent/20 rounded-md"
            >
              <i className="fa-regular fa-arrow-left text-xs" />
            </button>
            <h2 className="text-xl font-semibold text-foreground">{selectedOption.label}</h2>
            {selectedOption.required && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-primary/10 text-primary border border-primary/20">
                <i className="fa-regular fa-asterisk text-[8px]" />
                Required
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground mt-1">{selectedOption.description}</p>
        </div>
        <div className="flex-1 min-h-0">
          <AIPromptDetail subcategory={modifiedSubcategory} />
        </div>
      </div>
    );
  }

  // @ts-expect-error - userPreferences is dynamically set in bryntumContext
  const hasOpenAIKey = userPreferences.getSetting(PERSISTENCE_STATE_KEYS.hasOpenAiApiKey, "false", true) === "true";

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="px-6 pt-6 pb-4 border-b border-border flex-shrink-0">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-foreground mb-2">{subcategory.label}</h2>
            <p className="text-sm text-muted-foreground">{subcategory.description}</p>
          </div>
          {hasOpenAIKey && (
            <div className="flex gap-2 flex-shrink-0">
              <RevertToFactoryButton inspectionType={inspectionType} advisorType={advisorType} promoteType={promoteType} />
              <TestPromptButton inspectionType={inspectionType} advisorType={advisorType} promoteType={promoteType} />
            </div>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        <div className="grid grid-cols-1 gap-4">
          {promptOptions.map((option) => (
            <div
              key={option.id}
              role="button"
              onClick={() => setSelectedOption(option)}
              className={cn(
                "flex items-start justify-between gap-4 group relative p-4 rounded-lg border bg-card cursor-pointer transition-all duration-200 ease-out hover:border-primary/30 hover:bg-primary/5",
                "border-border"
              )}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="font-semibold text-foreground text-base">{option.label}</h3>
                  {option.required && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-primary/10 text-primary border border-primary/20">
                      <i className="fa-regular fa-asterisk text-[8px]" />
                      Required
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">{option.description}</p>
              </div>
              <ChevronRight className={cn(
                "h-5 w-5 text-muted-foreground group-hover:text-foreground transition-colors flex-shrink-0 mt-1"
              )} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AIPromptSelector;
